/* eslint-disable @typescript-eslint/no-explicit-any */

export const URL_KEYS = new Set<string>([
  'url',
  'thumb_url',
  'active_thumb',
  'preview_url',
  'poster',
  'src',
  'thumbnail_url',
  'thumb',
  'image',
])

export const DEFAULT_SIGNED_URL_TTL_MINUTES = 60
export const DEFAULT_REFRESH_BUFFER_MINUTES = 10

function safeParseUrl(url: string): URL | null {
  try {
    return new URL(url)
  } catch {
    return null
  }
}

export function canonicalizeUrl(url?: string | null): string | null {
  if (!url) return null
  const s = String(url).trim()
  if (!s) return null

  const u = safeParseUrl(s)
  if (u) return u.origin + u.pathname

  const q = s.indexOf('?')
  const h = s.indexOf('#')
  const cut = q >= 0 && h >= 0 ? Math.min(q, h) : q >= 0 ? q : h
  return cut >= 0 ? s.slice(0, cut) : s
}

export function isSignedUrl(url?: string | null): boolean {
  if (!url) return false
  const s = String(url)
  return /(?:\?|&)(Policy|Signature|Key-Pair-Id|Expires|X-Amz-Algorithm|X-Amz-Credential|X-Amz-Signature|X-Amz-Expires)=/i.test(
    s
  )
}

export function computeExpiresAt(ttlMinutes = DEFAULT_SIGNED_URL_TTL_MINUTES): number {
  return Date.now() + ttlMinutes * 60 * 1000
}

export function shouldRefreshSignedUrl(
  expiresAt?: number | null,
  bufferMinutes = DEFAULT_REFRESH_BUFFER_MINUTES
): boolean {
  if (!expiresAt) return true
  const bufferMs = bufferMinutes * 60 * 1000
  return Date.now() >= expiresAt - bufferMs
}

function base64UrlToBase64(s: string): string {
  let out = s.replace(/-/g, '+').replace(/_/g, '/')
  const pad = out.length % 4
  if (pad === 2) out += '=='
  else if (pad === 3) out += '='
  else if (pad === 1) out += '==='
  return out
}

export function getCloudFrontPolicyExpiryMs(url?: string | null): number | null {
  if (!url) return null
  const u = safeParseUrl(String(url))
  if (!u) return null

  const policy = u.searchParams.get('Policy')
  if (!policy) return null

  try {
    const b64 = base64UrlToBase64(policy)
    const json = atob(b64)
    const m = json.match(/"AWS:EpochTime"\s*:\s*(\d+)/)
    if (!m) return null

    const sec = Number(m[1])
    if (!Number.isFinite(sec) || sec <= 0) return null
    return sec * 1000
  } catch {
    return null
  }
}

export function getCloudFrontExpiresParamMs(url?: string | null): number | null {
  if (!url) return null
  const u = safeParseUrl(String(url))
  if (!u) return null

  const exp = u.searchParams.get('Expires')
  if (!exp) return null

  const sec = Number(exp)
  if (!Number.isFinite(sec) || sec <= 0) return null
  return sec * 1000
}

export function getSignedUrlExpiryMs(url?: string | null): number | null {
  if (!url) return null
  if (!isSignedUrl(url)) return null

  const policyMs = getCloudFrontPolicyExpiryMs(url)
  if (policyMs) return policyMs

  const expiresMs = getCloudFrontExpiresParamMs(url)
  if (expiresMs) return expiresMs

  return null
}

export function findMinSignedExpiryMs(input: any): number | null {
  let min: number | null = null

  const visit = (v: any) => {
    if (v == null) return

    if (typeof v === 'string') {
      const exp = getSignedUrlExpiryMs(v)
      if (exp && (min == null || exp < min)) min = exp
      return
    }

    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) visit(v[i])
      return
    }

if (typeof v === 'object') {
  for (const k of Object.keys(v)) {
    const val = v[k]

    if (
      k === 'cloudfront' &&
      val &&
      typeof val === 'object'
    ) {
      const expiresAt = Number(val.expires_at)

      if (
        Number.isFinite(expiresAt) &&
        expiresAt > 0
      ) {
        const expiryMs = expiresAt * 1000

        if (min === null || expiryMs < min) {
          min = expiryMs
        }
      }

      continue
    }

    if (typeof val === 'string' && URL_KEYS.has(k)) {
          const exp = getSignedUrlExpiryMs(val)
          if (exp && (min == null || exp < min)) min = exp
          continue
        }

        if (typeof val === 'object') visit(val)
      }
    }
  }

  visit(input)
  return min
}

export function decorateMediaUrls<T>(input: T): T {
  if (input == null) return input

  const t = typeof input
  if (t !== 'object') return input

  if (Array.isArray(input)) {
    const out = new Array(input.length)
    for (let i = 0; i < input.length; i++) {
      out[i] = decorateMediaUrls((input as any)[i])
    }
    return out as any
  }

  const obj: any = input
  const out: any = {}

  for (const k of Object.keys(obj)) {
    const v = obj[k]

    if (v == null) {
      out[k] = v
      continue
    }

    if (typeof v === 'string' && URL_KEYS.has(k)) {
      out[k] = v
      out[`${k}_canonical`] = canonicalizeUrl(v)
      continue
    }

    if (typeof v === 'object') {
      out[k] = decorateMediaUrls(v)
      continue
    }

    out[k] = v
  }

  return out
}

function mergeUrlField(
  cachedUrl: any,
  freshUrl: any,
  cachedExpiresAt?: number | null,
  refreshBufferMinutes = DEFAULT_REFRESH_BUFFER_MINUTES
) {
  if (typeof freshUrl !== 'string') return freshUrl
  if (typeof cachedUrl !== 'string') return freshUrl

  const cachedCanonical = canonicalizeUrl(cachedUrl)
  const freshCanonical = canonicalizeUrl(freshUrl)

  if (!cachedCanonical || !freshCanonical || cachedCanonical !== freshCanonical) {
    return freshUrl
  }

  const cachedRealExp = getSignedUrlExpiryMs(cachedUrl)
  const effectiveExpiresAt = cachedRealExp ?? cachedExpiresAt ?? null

/*   console.log(
    `[SIGNED MERGE] ${new Date().toISOString()} canonicalSame=true ` +
      `policyExp=${cachedRealExp ? new Date(cachedRealExp).toISOString() : 'null'} ` +
      `storeExp=${cachedExpiresAt ? new Date(cachedExpiresAt).toISOString() : 'null'} ` +
      `effective=${effectiveExpiresAt ? new Date(effectiveExpiresAt).toISOString() : 'null'} ` +
      `refresh=${shouldRefreshSignedUrl(effectiveExpiresAt, refreshBufferMinutes)}`
  ) */

  if (!shouldRefreshSignedUrl(effectiveExpiresAt, refreshBufferMinutes)) {
    return cachedUrl
  }

  return freshUrl
}

export function deepMergeSignedUrls<T>(
  cached: T,
  fresh: T,
  cachedExpiresAt?: number | null,
  refreshBufferMinutes = DEFAULT_REFRESH_BUFFER_MINUTES
): T {
  if (fresh == null) return fresh
  if (cached == null) return fresh

  const tf = typeof fresh
  const tc = typeof cached

  if (tf !== 'object' || tc !== 'object') {
    return fresh
  }

  if (Array.isArray(fresh) && Array.isArray(cached)) {
    const cachedById = new Map(
      (cached as any[])
        .filter(item => item && typeof item === 'object' && item.id != null)
        .map(item => [String(item.id), item])
    )

    return (fresh as any[]).map((item, index) => {
      const hasId =
        item &&
        typeof item === 'object' &&
        item.id != null

      const cachedItem = hasId
        ? cachedById.get(String(item.id))
        : (cached as any[])[index]

      return deepMergeSignedUrls(
        cachedItem,
        item,
        cachedExpiresAt,
        refreshBufferMinutes
      )
    }) as T
  }

  if (!Array.isArray(fresh) && !Array.isArray(cached)) {
    const out: any = {}
    const keys = new Set<string>([...Object.keys(cached as any), ...Object.keys(fresh as any)])

    keys.forEach((k) => {
      const f = (fresh as any)[k]
      const c = (cached as any)[k]

      if (typeof f === 'string' && URL_KEYS.has(k)) {
        out[k] = mergeUrlField(c, f, cachedExpiresAt, refreshBufferMinutes)
        out[`${k}_canonical`] = canonicalizeUrl(out[k])
        return
      }

      if (k.endsWith('_canonical') && URL_KEYS.has(k.replace(/_canonical$/, ''))) {
        return
      }

      if (typeof f === 'object' && f != null) {
        out[k] = deepMergeSignedUrls(c, f, cachedExpiresAt, refreshBufferMinutes)
        return
      }

      out[k] = f
    })

    return out
  }

  return fresh
}

export function normalizeSignedPayload<T>(
  input: T,
  ttlMinutes = DEFAULT_SIGNED_URL_TTL_MINUTES
): { data: T; expiresAt: number } {
  return {
    data: decorateMediaUrls(input),
    expiresAt: computeExpiresAt(ttlMinutes),
  }
}